import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import { buildApiUrl, buildAssetUrl } from '../config/constants';

interface BlogPost {
  id: number;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  status: string;
  isPromoted: boolean;
  featuredImage?: string;
  readTime: number;
  viewCount: number;
  publishedAt: string;
  createdAt: string;
  metaDescription?: string;
  metaKeywords?: string;
  author: {
    id: number;
    username: string;
    email: string;
  };
  category?: {
    id: number;
    name: string;
    slug: string;
    color: string;
  };
  tags: Array<{
    id: number;
    name: string;
    slug: string;
    color: string;
  }>;
}

interface BlogPostResponse {
  post: BlogPost;
  relatedPosts: BlogPost[];
}

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  
  const [post, setPost] = useState<BlogPost | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (slug) {
      fetchPost(slug);
    }
  }, [slug]);

  const fetchPost = async (postSlug: string) => {
    try {
      setLoading(true);
      setError('');

      const response = await fetch(buildApiUrl(`/blog/${postSlug}`));
      
      if (!response.ok) {
        if (response.status === 404) {
          setError('Postarea nu a fost găsită');
        } else {
          throw new Error('Eroare la încărcarea postării');
        }
        return;
      }

      const data = await response.json();
      
      if (data.success) {
        setPost(data.data.post);
        setRelatedPosts(data.data.relatedPosts || []);
      } else {
        setError('Eroare la încărcarea postării');
      }
    } catch (error) {
      console.error('Eroare la încărcarea postării:', error);
      setError('Nu s-a putut încărca postarea');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ro-RO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatContent = (content: string) => {
    return content
      .split('\n\n')
      .map((paragraph, index) => (
        <p key={index} className="mb-6 text-gray-800 leading-relaxed text-lg">
          {paragraph}
        </p>
      ));
  };

  const handleTagClick = (tagSlug: string) => {
    navigate(`/blog?tag=${tagSlug}`);
  };

  const handleCategoryClick = (categorySlug: string) => {
    navigate(`/blog?category=${categorySlug}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="pt-20 pb-16">
          <div className="container mx-auto px-4">
            <div className="text-center py-16">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
              <p className="text-gray-600">Se încarcă postarea...</p>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="pt-20 pb-16">
          <div className="container mx-auto px-4">
            <div className="text-center py-16">
              <div className="text-6xl text-gray-300 mb-6">📝</div>
              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                {error || 'Postarea nu a fost găsită'}
              </h1>
              <p className="text-gray-600 mb-8 text-lg">
                Postarea pe care o căutați nu există sau a fost ștearsă.
              </p>
              <Link
                to="/blog"
                className="inline-block bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Înapoi la Blog
              </Link>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="pt-20 pb-16">
        <div className="container mx-auto px-4">
          {/* Breadcrumb */}
          <nav className="mb-8">
            <div className="flex items-center space-x-2 text-sm text-gray-500">
              <Link to="/" className="hover:text-blue-600 transition-colors">Acasă</Link>
              <span>/</span>
              <Link to="/blog" className="hover:text-blue-600 transition-colors">Blog</Link>
              {post.category && (
                <>
                  <span>/</span>
                  <button
                    onClick={() => handleCategoryClick(post.category!.slug)}
                    className="hover:text-blue-600 transition-colors"
                  >
                    {post.category.name}
                  </button>
                </>
              )}
              <span>/</span>
              <span className="text-gray-900 font-medium">{post.title}</span>
            </div>
          </nav>

          <article className="max-w-4xl mx-auto">
            {/* Article Header */}
            <header className="mb-12">
              {post.category && (
                <button
                  onClick={() => handleCategoryClick(post.category!.slug)}
                  className="inline-block px-4 py-2 rounded-full text-white text-sm font-medium mb-6 hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: post.category.color }}
                >
                  {post.category.name}
                </button>
              )}
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                {post.title}
              </h1>
              
              {post.excerpt && (
                <p className="text-xl md:text-2xl text-gray-600 mb-8 leading-relaxed">
                  {post.excerpt}
                </p>
              )}

              <div className="flex flex-wrap items-center gap-6 text-gray-500">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold mr-3">
                    {post.author.username.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className="text-sm">de <strong className="text-gray-700">{post.author.username}</strong></div>
                  </div>
                </div>
                <div className="text-sm">{formatDate(post.publishedAt)}</div>
                <div className="text-sm">{post.readTime} min citire</div>
                <div className="text-sm">{post.viewCount} vizualizări</div>
              </div>
            </header>

            {/* Featured Image */}
            {post.featuredImage && (
              <div className="mb-12">
                <img
                  src={buildAssetUrl(post.featuredImage)}
                  alt={post.title}
                  className="w-full h-64 md:h-96 lg:h-[500px] object-cover rounded-xl shadow-lg"
                />
              </div>
            )}

            {/* Article Content */}
            <div className="mb-12">
              <div className="prose prose-xl max-w-none">
                {formatContent(post.content)}
              </div>
            </div>

            {/* Tags */}
            {post.tags.length > 0 && (
              <div className="mb-12">
                <h3 className="text-xl font-semibold text-gray-900 mb-6">Tag-uri</h3>
                <div className="flex flex-wrap gap-3">
                  {post.tags.map((tag) => (
                    <button
                      key={tag.id}
                      onClick={() => handleTagClick(tag.slug)}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-full text-sm hover:bg-gray-200 transition-colors font-medium"
                    >
                      #{tag.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Author Info */}
            <div className="bg-white rounded-xl p-8 mb-12 shadow-sm">
              <div className="flex items-center">
                <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center text-white text-2xl font-bold mr-6">
                  {post.author.username.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {post.author.username}
                  </h3>
                  <p className="text-gray-600">Autor</p>
                </div>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-12 pb-8 border-b border-gray-200">
              <Link
                to="/blog"
                className="inline-flex items-center text-blue-600 hover:text-blue-700 transition-colors font-medium"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Înapoi la Blog
              </Link>
              
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => window.print()}
                  className="p-2 text-gray-600 hover:text-gray-800 transition-colors rounded-lg hover:bg-gray-100"
                  title="Printează articolul"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                  </svg>
                </button>
                
                <button
                  onClick={() => {
                    if (navigator.share) {
                      navigator.share({
                        title: post.title,
                        text: post.excerpt,
                        url: window.location.href
                      });
                    } else {
                      navigator.clipboard.writeText(window.location.href);
                      alert('Link copiat în clipboard!');
                    }
                  }}
                  className="p-2 text-gray-600 hover:text-gray-800 transition-colors rounded-lg hover:bg-gray-100"
                  title="Distribuie articolul"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                  </svg>
                </button>
              </div>
            </div>
          </article>

          {/* Related Posts */}
          {relatedPosts.length > 0 && (
            <section className="mt-16">
              <div className="max-w-6xl mx-auto">
                <h2 className="text-3xl font-bold text-gray-900 mb-10 text-center">Articole Similare</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {relatedPosts.map((relatedPost) => (
                    <Link
                      key={relatedPost.id}
                      to={`/blog/${relatedPost.slug}`}
                      className="group block bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
                    >
                      {relatedPost.featuredImage && (
                        <div className="overflow-hidden">
                          <img
                            src={buildAssetUrl(relatedPost.featuredImage)}
                            alt={relatedPost.title}
                            className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                      )}
                      <div className="p-6">
                        {relatedPost.category && (
                          <span 
                            className="inline-block px-3 py-1 rounded-full text-white text-xs font-medium mb-3"
                            style={{ backgroundColor: relatedPost.category.color }}
                          >
                            {relatedPost.category.name}
                          </span>
                        )}
                        <h3 className="text-lg font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors line-clamp-2">
                          {relatedPost.title}
                        </h3>
                        {relatedPost.excerpt && (
                          <p className="text-gray-600 mb-4 text-sm line-clamp-3">
                            {relatedPost.excerpt}
                          </p>
                        )}
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <span className="font-medium">{relatedPost.author.username}</span>
                          <div className="flex items-center space-x-2">
                            <span>{relatedPost.readTime} min</span>
                            <span>•</span>
                            <span>{relatedPost.viewCount} vizualizări</span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BlogPost; 
